'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import React, { useContext, useEffect, useState } from 'react';
import { Modal, Text, Button, Flex, Box, Menu } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

// Context
import { WorkspaceLayoutContext } from '@app/layouts/WorkspaceLayout/context';

// Types
import { WorkspaceStatusType, useGetMeQuery } from '@app/types/blackroom-api';

// Icons
import WarningIcon from '@app/shared/ui-kit/icons/WarningIcon';
import {
  IconLogout,
  IconDotsVertical,
  IconSwitchHorizontal,
} from '@tabler/icons-react';

// UI constants
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';

// Styles
import { palette } from '@app/shared/ui-kit/colors';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';

// View-as
import { setViewAsUser } from '@app/shared/providers/ViewAsProvider/store';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

interface AppSubscriptionProviderProps {
  children: React.ReactNode;
}

export default function AppSubscriptionProvider({
  children,
}: AppSubscriptionProviderProps) {
  /* ********************************** HOOKS ********************************* */

  const { t } = useTranslation(['Common', 'Workspace']);
  const navigate = useNavigate();
  const workspaceContext = useContext(WorkspaceLayoutContext);
  const { workspace } = workspaceContext;
  const { data: meData } = useGetMeQuery();
  const { logout } = useAuth0();
  const [opened, setOpened] = useState(false);

  // State for modal visibility
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasAlternativeWorkspace, setHasAlternativeWorkspace] = useState(false);

  /* ******************************** CALLBACKS ******************************* */

  const handleSwitchWorkspace = () => {
    setOpened(false);
    // Get available workspaces
    const userWorkspaces = (meData?.me.workspaceUsers ?? []).map(
      (wu) => wu.workspace,
    );

    // Find a workspace with active payment or offline status (excluding current workspace)
    const workspaceWithActivePayment = userWorkspaces.find(
      (w) =>
        w.id !== workspace?.id &&
        (w.isPaymentActive || w.status === WorkspaceStatusType.Offline),
    );

    if (workspaceWithActivePayment) {
      navigate(`/workspace/${workspaceWithActivePayment.id}/resources/my`);
    }
  };

  /* ******************************** SIDE EFFECTS ***************************** */

  useEffect(() => {
    if (!workspace || !meData?.me) {
      setShowPaymentModal(false);
      setHasAlternativeWorkspace(false);
      return;
    }

    // Check if payment is not active and workspace is live
    const shouldShowModal =
      !workspace.isPaymentActive &&
      workspace.status === WorkspaceStatusType.Live;

    // Check if there's an alternative workspace with active payment
    const userWorkspaces = (meData.me.workspaceUsers ?? []).map(
      (wu) => wu.workspace,
    );
    const hasAlternative = userWorkspaces.some(
      (w) =>
        w.id !== workspace.id &&
        (w.isPaymentActive || w.status === WorkspaceStatusType.Offline),
    );

    setShowPaymentModal(shouldShowModal);
    setHasAlternativeWorkspace(hasAlternative);
  }, [
    workspace?.isPaymentActive,
    workspace?.status,
    workspace?.id,
    meData?.me,
  ]);

  /* ******************************** RENDERING ******************************* */

  return (
    <>
      {children}

      {/* Non-closable Payment Modal */}
      <Modal
        opened={showPaymentModal}
        onClose={() => {}} // Empty function - modal cannot be closed
        withCloseButton={false} // Remove close button
        closeOnClickOutside={false} // Prevent closing on outside click
        closeOnEscape={false} // Prevent closing with escape key
        trapFocus={true}
        centered
        size={hasAlternativeWorkspace ? 'lg' : 'md'}
        zIndex={9999} // Ensure it's on top of everything
      >
        <Flex direction="column">
          {/* Red header bar */}
          <Box bg="blackroomRed" h="xs" w="100%" mb="lg" />

          {/* Content */}
          <Flex direction="column" align="center" gap="md" px="xl" ta="center">
            <WarningIcon color={palette.blackroomRed} size={64} />

            <Text
              size="xl"
              ta="center"
              fw={FONT_WEIGHT.bold}
              c={palette.blackroomRed}
            >
              {t('Workspace:shared.paymentRequired.title')}
            </Text>

            <Text
              size="md"
              ta="center"
              c={palette.blackroomDarkGray3}
              dangerouslySetInnerHTML={{
                __html: t('Workspace:shared.paymentRequired.message'),
              }}
            />
          </Flex>

          <Flex direction="row" gap="md" justify="center" p="lg">
            <Button
              color="blackroomRed"
              size="md"
              onClick={() => {
                window.location.href = 'mailto:support@blackroom.cc';
              }}
            >
              {t('Workspace:shared.paymentRequired.contactSupport')}
            </Button>

            <Menu shadow="md" position="top" opened={opened} zIndex={10000}>
              <Menu.Target>
                <Button
                  variant="outline"
                  color="blackroomGray"
                  size="md"
                  rightSection={
                    <IconDotsVertical size={ICON_SIZE_MAP.smallavg} />
                  }
                  onClick={() => setOpened(!opened)}
                >
                  {t('Common:placeholders.other')}
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                {hasAlternativeWorkspace && (
                  <Menu.Item
                    leftSection={
                      <IconSwitchHorizontal size={ICON_SIZE_MAP.smallavg} />
                    }
                    onClick={handleSwitchWorkspace}
                  >
                    {t('Workspace:shared.paymentRequired.switchWorkspace')}
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={<IconLogout size={ICON_SIZE_MAP.smallavg} />}
                  onClick={() => {
                    setOpened(!opened);
                    setViewAsUser(null);
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    });
                  }}
                >
                  {t('WorkspaceLayout:UserMenu.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
